import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import {
  Content,
  QueryContentDto,
  QueryMagazineDto,
  Magazine,
  BlogArticle,
  QueryBlogArticleDto,
  QueryMediaDto,
  Media,
  ContentCategory,
  QueryContentCategoryDto,
} from "./types";
import { RootState } from "../../../store";
import { ChevronsRightLeft } from "lucide-react";

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ADMIN_CMS_APP_API_PERFIX,
]
  .filter(Boolean)
  .join("");

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("Warning: REACT_APP_API_BASE_URL is not defined.");
}
if (!process.env.REACT_APP_ADMIN_CMS_APP_API_PERFIX) {
  console.warn(
    "Warning: REACT_APP_ADMIN_CMS_APP_API_PERFIX is not defined."
  );
}


export const fetchMagazines = createAsyncThunk<Magazine[], QueryMagazineDto>(
  "magazines/fetchAll",
  async (query) => {
    const filters: Record<string, any> = {};
    if (query.page) filters.page = query.page;
    if (query.limit) filters.limit = query.limit;
    if (query.website) filters.website = query.website;
    if (query.pageUrl) filters.pageUrl = query.pageUrl;

    const response = await axios.get(`${API_URL}/magazines`, {
      params: filters,
    });

    return response.data;
  }
);

export const fetchContents = createAsyncThunk<Content[], QueryContentDto>(
  "contents/fetchAll",
  async (query) => {
    const filters: Record<string, any> = {};
    if (query.page) filters.page = query.page;
    if (query.limit) filters.limit = query.limit;
    if (query.website) filters.website = query.website;
    if (query.pageUri) filters.pageUri = query.pageUri;
    if (query.displayName) filters.displayName = query.displayName;

    const response = await axios.get(`${API_URL}/content-manager`, {
      params: filters,
    });
    return response.data;
  }
);

export const fetchBlogArticles = createAsyncThunk<
  BlogArticle[],
  QueryBlogArticleDto
>("blogArticles/fetchAll", async (query) => {
  const filters: Record<string, any> = {};
  if (query.page) filters.page = query.page;
  if (query.limit) filters.limit = query.limit;
  if (query.website) filters.website = query.website;
  if (query.title) filters.title = query.title;
  if (query.pageUrl) filters.pageUrl = query.pageUrl;

  const response = await axios.get(`${API_URL}/blog-articles`, {
    params: filters,
  });
  return response.data;
});

export const fetchMedia = createAsyncThunk<Media[], QueryMediaDto>(
  "media/fetchAll",
  async (query) => {
    const filters: Record<string, any> = {};
    if (query.page) filters.page = query.page;
    if (query.limit) filters.limit = query.limit;
    if (query.websiteName) filters.websiteName = query.websiteName;
    if (query.mediaType) filters.mediaType = query.mediaType;
    if (query.mediaName) filters.mediaName = query.mediaName;

    const response = await axios.get(`${API_URL}/media`, { params: filters });
    return response.data;
  }
);

export const fetchMagazinesbyid = createAsyncThunk<Magazine, string>(
  "magazines/fetchById",
  async (id) => {
    console.log("id", id);

    const response = await axios.get(`${API_URL}/magazines/${id}`);
    return response.data;
  }
);

export const fetchContentsbyid = createAsyncThunk<Content, string>(
  "contents/fetchById",
  async (id) => {
    const response = await axios.get(`${API_URL}/content-manager/${id}`);
    return response.data;
  }
);

export const fetchBlogArticlesbyid = createAsyncThunk<BlogArticle, string>(
  "blogArticles/fetchById",
  async (id) => {
    const response = await axios.get(`${API_URL}/blog-articles/${id}`);
    return response.data;
  }
);

export const fetchMediabyid = createAsyncThunk<Media, string>(
  "media/fetchById",
  async (id) => {
    const response = await axios.get(`${API_URL}/media/${id}`);
    return response.data;
  }
);

export const fetchContentCategories = createAsyncThunk<
  ContentCategory[],
  QueryContentCategoryDto
>("contentCategories/fetchAll", async (query) => {
  const response = await axios.get(`${API_URL}/content-categories`, {
    params: query,
  });
  return response.data;
});

export const fetchContentCategoryById = createAsyncThunk<
  ContentCategory,
  string
>("contentCategories/fetchById", async (id) => {
  const response = await axios.get(`${API_URL}/content-categories/${id}`);
  return response.data;
});

interface CustomerCmsApisState {
  magazines: Magazine[];
  contents: Content[];
  blogArticles: BlogArticle[];
  contentCategories: ContentCategory[];
  currentContentCategory: ContentCategory | null;
  media: Media[];
  loading: boolean;
  error: string | null;
  currentMagazine: Magazine | null;
  currentContent: Content | null;
  currentBlogArticle: BlogArticle | null;
  currentMedia: Media | null;
  success: boolean;
}

const initialState: CustomerCmsApisState = {
  magazines: [],
  contents: [],
  blogArticles: [],
  contentCategories: [],
  media: [],
  currentContentCategory: null,
  loading: false,
  error: null,
  currentMagazine: null,
  currentContent: null,
  currentBlogArticle: null,
  currentMedia: null,
  success: false,
};

const customerCmsApisSlice = createSlice({
  name: "customerCmsApis",
  initialState,
  reducers: {
    clearCurrentMagazine: (state) => {
      state.currentMagazine = null;
    },
    clearCurrentContent: (state) => {
      state.currentContent = null;
    },
    clearCurrentBlogArticle: (state) => {
      state.currentBlogArticle = null;
    },
    clearCurrentMedia: (state) => {
      state.currentMedia = null;
    },
    clearCurrentContentCategory: (state) => {
      state.currentContentCategory = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all magazines
    builder.addCase(fetchMagazines.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMagazines.fulfilled, (state, action) => {
      state.loading = false;
      state.magazines = action.payload;
    });
    builder.addCase(fetchMagazines.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch magazines";
    });
    // Fetch all contents
    builder.addCase(fetchContents.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchContents.fulfilled, (state, action) => {
      state.loading = false;
      state.contents = action.payload;
    });
    builder.addCase(fetchContents.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch contents";
    });
    // Fetch all blog articles
    builder.addCase(fetchBlogArticles.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchBlogArticles.fulfilled, (state, action) => {
      state.loading = false;
      state.blogArticles = action.payload;
    });
    builder.addCase(fetchBlogArticles.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch blog articles";
    });
    // Fetch all media
    builder.addCase(fetchMedia.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMedia.fulfilled, (state, action) => {
      state.loading = false;
      state.media = action.payload;
    });
    builder.addCase(fetchMedia.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch media";
    });
    // Fetch magazine by id
    builder.addCase(fetchMagazinesbyid.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMagazinesbyid.fulfilled, (state, action) => {
      state.loading = false;
      state.currentMagazine = action.payload;
    });
    builder.addCase(fetchMagazinesbyid.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch magazine by id";
    });
    // Fetch content by id
    builder.addCase(fetchContentsbyid.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchContentsbyid.fulfilled, (state, action) => {
      state.loading = false;
      state.currentContent = action.payload;
    });
    builder.addCase(fetchContentsbyid.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch content by id";
    });
    // Fetch blog article by id
    builder.addCase(fetchBlogArticlesbyid.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchBlogArticlesbyid.fulfilled, (state, action) => {
      state.loading = false;
      state.currentBlogArticle = action.payload;
    });
    builder.addCase(fetchBlogArticlesbyid.rejected, (state, action) => {
      state.loading = false;
      state.error =
        action.error.message || "Failed to fetch blog article by id";
    });
    // Fetch media by id
    builder.addCase(fetchMediabyid.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMediabyid.fulfilled, (state, action) => {
      state.loading = false;
      state.currentMedia = action.payload;
    });
    builder.addCase(fetchMediabyid.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch media by id";
    });
    // Fetch all content categories
    builder.addCase(fetchContentCategories.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchContentCategories.fulfilled, (state, action) => {
      state.loading = false;
      state.contentCategories = action.payload;
    });
    builder.addCase(fetchContentCategories.rejected, (state, action) => {
      state.loading = false;
      state.error =
        action.error.message || "Failed to fetch content categories";
    });
    // Fetch content category by id
    builder.addCase(fetchContentCategoryById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchContentCategoryById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentContentCategory = action.payload;
    });
    builder.addCase(fetchContentCategoryById.rejected, (state, action) => {
      state.loading = false;
      state.error =
        action.error.message || "Failed to fetch content category by id";
    });
  },
});

export const {
  clearCurrentMagazine,
  clearCurrentContent,
  clearCurrentBlogArticle,
  clearCurrentMedia,
  clearCurrentContentCategory,
} = customerCmsApisSlice.actions;
export default customerCmsApisSlice.reducer;
